import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import ApplicationsTable from "@/components/admin/membership/ApplicationsTable";
import ApplicationDetailDialog from "@/components/admin/membership/ApplicationDetailDialog";
import { downloadApplicationPDF } from "@/components/admin/membership/pdfUtils";
import { MembershipApplication } from "@/components/admin/membership/types";
import { queryClient } from "@/lib/react-query";

const AdminMembershipApplications = () => {
  const { toast } = useToast();
  const [selectedApplication, setSelectedApplication] = useState<MembershipApplication | null>(null);
  const [localApplications, setLocalApplications] = useState<MembershipApplication[]>([]);
  const [statusUpdating, setStatusUpdating] = useState<boolean>(false);
  
  // Use staleTime: 0 to ensure we always get fresh data on reload
  const { data: applications, refetch, isLoading } = useQuery({
    queryKey: ["membership-applications"],
    queryFn: async () => {
      console.log("[AdminMembershipApplications] Fetching applications");
      const { data, error } = await supabase
        .from("membership_applications")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) {
        console.error("[AdminMembershipApplications] Error:", error);
        throw error;
      }
      
      return data as MembershipApplication[];
    },
    staleTime: 0, // Make sure we always get fresh data
  });

  useEffect(() => {
    if (applications) {
      console.log("[AdminMembershipApplications] Updating local applications state:", applications);
      setLocalApplications(applications);
    }
  }, [applications]);

  const updateApplicationStatus = async (id: string, status: string) => {
    if (statusUpdating) return;
    
    console.log(`[AdminMembershipApplications] Updating application ${id} status to ${status}`);
    setStatusUpdating(true);
    
    try {
      // First, update the database
      const { error, data } = await supabase
        .from("membership_applications")
        .update({ status })
        .eq("id", id)
        .select()
        .single();
      
      if (error) {
        console.error("[AdminMembershipApplications] Status update error:", error);
        toast({
          title: "Error",
          description: `Failed to update application status: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      }

      console.log("[AdminMembershipApplications] Updated application in database:", data);

      // After successful DB update, update UI state with the returned data
      const updatedApplications = localApplications.map(app => 
        app.id === id ? { ...app, status } : app
      );
      
      setLocalApplications(updatedApplications);
      
      // Update selected application if it's the one being changed
      if (selectedApplication?.id === id) {
        setSelectedApplication(prev => prev ? { ...prev, status } : null);
      }
      
      // Update react-query cache with the fresh data we got back
      queryClient.setQueryData(
        ["membership-applications"],
        updatedApplications
      );
      
      // Force a refetch to ensure we have the latest data
      await refetch();
      
      toast({
        title: "Status Updated",
        description: `Application status has been updated to ${status}`,
      });
      
      console.log(`[AdminMembershipApplications] Application ${id} status successfully updated to ${status}`);
    } catch (error) {
      console.error("[AdminMembershipApplications] Error updating status:", error);
    } finally {
      setStatusUpdating(false);
    }
  };

  // Realtime subscription for updates to membership_applications
  useEffect(() => {
    const channel = supabase
      .channel('membership-applications-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'membership_applications'
        },
        async (payload) => {
          console.log("[AdminMembershipApplications] Update detected:", payload);
          
          // Force a refetch whenever there's an update to ensure we have the latest data
          await refetch();
          
          // Ensure we have the complete updated application with all fields
          const updatedApp = payload.new as MembershipApplication;
          
          // Update local state as well
          setLocalApplications(prevApps => 
            prevApps.map(app => 
              app.id === updatedApp.id ? updatedApp : app
            )
          );
          
          if (selectedApplication && selectedApplication.id === updatedApp.id) {
            setSelectedApplication(updatedApp);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedApplication, refetch]);

  // Subscription for new applications
  useEffect(() => {
    const channel = supabase
      .channel('membership-applications-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'membership_applications'
        },
        async (payload) => {
          const newApplication = payload.new as MembershipApplication;
          
          // Force a refetch to get all applications including the new one
          await refetch();
          
          toast({
            title: "New Membership Application",
            description: `From: ${newApplication.first_name} ${newApplication.last_name} - ${newApplication.business_name}`,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [toast, refetch]);

  return (
    <div className="min-h-screen py-24">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Membership Applications</h1>
        
        {isLoading ? (
          <div className="text-center py-8">Loading applications...</div>
        ) : (
          <ApplicationsTable 
            applications={localApplications}
            onViewDetails={(application) => setSelectedApplication(application)}
            onDownloadPDF={downloadApplicationPDF}
            onStatusChange={updateApplicationStatus}
          />
        )}
      </div>

      <ApplicationDetailDialog 
        application={selectedApplication}
        onOpenChange={() => setSelectedApplication(null)}
        onStatusChange={updateApplicationStatus}
      />
    </div>
  );
};

export default AdminMembershipApplications;
